const RETRIABLE_STATUSES = new Set([408, 429, 502, 503, 504]);
const SAFE_METHODS = new Set(["GET", "HEAD"]);

function delay(milliseconds, signal) {
  return new Promise((resolve, reject) => {
    const timeout = globalThis.setTimeout(resolve, milliseconds);
    signal?.addEventListener("abort", () => {
      globalThis.clearTimeout(timeout);
      reject(signal.reason || new DOMException("Aborted", "AbortError"));
    }, { once: true });
  });
}

function requestError(message, code, status = 0, requestId = "") {
  const error = new Error(message);
  error.code = code;
  error.status = status;
  error.requestId = requestId;
  return error;
}

export async function apiRequest(path, options = {}) {
  const method = String(options.method || "GET").toUpperCase();
  const retries = SAFE_METHODS.has(method)
    ? Math.max(0, Number.isInteger(options.retry) ? options.retry : 1)
    : 0;
  const timeoutMs = Math.max(1_000, Number(options.timeoutMs) || (SAFE_METHODS.has(method) ? 12_000 : 20_000));
  const { retry: _retry, timeoutMs: _timeoutMs, signal: callerSignal, ...fetchOptions } = options;
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  let response;
  let payload;
  let upstreamCode = '';

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const controller = new AbortController();
    let timedOut = false;
    const abortFromCaller = () => controller.abort(callerSignal.reason || new DOMException("Aborted", "AbortError"));
    if (callerSignal?.aborted) abortFromCaller();
    else callerSignal?.addEventListener("abort", abortFromCaller, { once: true });
    const timeout = globalThis.setTimeout(() => {
      timedOut = true;
      controller.abort(new DOMException("Request timed out", "TimeoutError"));
    }, timeoutMs);
    try {
      response = await fetch(path, {
        credentials: "same-origin",
        ...fetchOptions,
        signal: controller.signal,
        headers: {
          ...(isFormData ? {} : { "content-type": "application/json" }),
          "x-n9-client": "n9-developer-tools-web",
          ...(options.headers || {}),
        },
      });
      const contentType = response.headers.get("content-type") || "";
      const isJson = contentType.includes("application/json");
      if (isJson || !response.ok) {
        let abortBody;
        try {
          const body = await Promise.race([
            isJson ? response.json() : response.text(),
            new Promise((_, reject) => {
              abortBody = () => reject(controller.signal.reason);
              controller.signal.addEventListener("abort", abortBody, { once: true });
              if (controller.signal.aborted) abortBody();
            }),
          ]);
          payload = isJson ? body : null;
          upstreamCode = isJson ? '' : (body.slice(0, 64000).match(/(?:error\s*(?:code)?\s*[:\s>]*)\b(1101|1102|1027|1015)\b/i)?.[1] || '');
        } finally { controller.signal.removeEventListener("abort", abortBody); }
      } else if (response.ok && response.status !== 204) {
        throw new SyntaxError("Expected JSON response");
      } else payload = null;
    } catch (cause) {
      if (timedOut) {
        if (attempt < retries) {
          await delay(300 * (attempt + 1), callerSignal);
          continue;
        }
        throw requestError("تأخر رد خدمة الدخول أو المحرك. أُوقف الانتظار ويمكنك إعادة المحاولة.", "REQUEST_TIMEOUT");
      }
      if (cause?.name === "AbortError" || callerSignal?.aborted) throw cause;
      if (cause instanceof SyntaxError) {
        throw requestError("وصل رد غير مكتمل من الخدمة. لم يتم تأكيد العملية؛ أعد المحاولة.", "INVALID_RESPONSE", response?.status);
      }
      if (attempt < retries) {
        await delay(300 * (attempt + 1), callerSignal);
        continue;
      }
      throw requestError("تعذر الاتصال بمنصة أدوات المطور. تحقق من الشبكة ثم أعد المحاولة.", "NETWORK_ERROR");
    } finally {
      globalThis.clearTimeout(timeout);
      callerSignal?.removeEventListener("abort", abortFromCaller);
    }
    if (!response.ok && RETRIABLE_STATUSES.has(response.status) && attempt < retries) {
      await response.body?.cancel?.().catch(() => undefined);
      await delay(300 * (attempt + 1), callerSignal);
      continue;
    }
    break;
  }

  if (!response.ok || payload?.ok === false) {
    const suppliedMessage = typeof payload?.error === 'string' ? payload.error : payload?.error?.message;
    const fallback = upstreamCode === '1102'
      ? 'أوقف خادم المنصة الطلب بسبب تجاوز موارد التنفيذ. لم يتأكد إنشاء الدفعة؛ راجع سجل الوظائف قبل إعادة المحاولة.'
      : `تعذر إكمال الطلب من الخدمة (HTTP ${response.status})${upstreamCode ? ` · Cloudflare ${upstreamCode}` : ''}.`;
    const error = new Error(suppliedMessage || fallback);
    error.code = payload?.error?.code || (upstreamCode === '1102' ? 'UPSTREAM_RESOURCE_LIMIT' : 'REQUEST_FAILED');
    error.status = response.status;
    error.requestId = payload?.error?.requestId || response.headers.get("x-request-id") || response.headers.get('cf-ray') || "";
    error.details = payload?.error?.details || null;
    error.retryAfterSeconds = Number(response.headers.get("retry-after")) || null;
    throw error;
  }
  return payload;
}
